import {
  AppBar,
  Badge,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Box,
  ButtonBase,
  Typography,
  Divider,
  ListItemIcon,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { HighBeamIcon } from '@/components/icons/HighBeamIcon';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailIcon from '@mui/icons-material/Email';
import MenuIcon from '@mui/icons-material/Menu';
import ChatIcon from '@mui/icons-material/Chat';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useState, memo, type ElementType } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import { uiLanguages, languageNames, type SupportedLanguage } from '@/lib/i18n';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useAssistantStore } from '@/stores/assistantStore';
import { GlobalSearch } from './GlobalSearch';
import { NotificationsMenu } from './NotificationsMenu';
import { AssistantToggleButton, ASSISTANT_PANEL_WIDTH, useAssistantSideBySide } from '@/components/assistant';
import { ProfileDialog } from '@/components/profile/ProfileDialog';
import { darkSurfaces } from '@/utils/themeBuilder';
import { UserAvatar } from '@/components/common';
import { useBranding } from '@/hooks/useBranding';
import { useSignOut } from '@/hooks/useAuth';
import { staffHelpEntries, type StaffHelpEntryKind } from './staffHelp';
import { ClockInButton } from '@/components/timetracking/ClockInButton';
import { useUnreadMessagesCount } from '@/hooks/useUnreadMessagesCount';
import { CopayaMenu } from '@/components/copaya/CopayaMenu';
import { WorkOrderWindowControl } from '@/components/workorders/WorkOrderWindowControl';
import { navigateAwayFromWorkOrderDetail } from '@/lib/workOrderNavigation';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar';
import { headerNavItems, isNavItemVisible } from './navigationModel';

/** These shortcuts are not the inspections module, so the flag cannot hide them. */
const HEADER_NAV_MODULES = { inspectionsEnabled: true };

export const HEADER_HEIGHT = 64;

const HELP_MENU_ICONS: Record<StaffHelpEntryKind, ElementType> = {
  osBuddyHelp: HelpOutlineIcon,
  tenantHelp: HelpOutlineIcon,
  oceanSpaceSupport: EmailIcon,
  tenantSupport: EmailIcon,
};

export const Header = memo(function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { t: tc } = useTranslation('common');
  const { t: tn } = useTranslation('navigation');
  const { t: tm } = useTranslation('messages');
  const { support } = useBranding();
  const themeMode = useUIStore((s) => s.themeMode);
  const toggleThemeMode = useUIStore((s) => s.toggleThemeMode);
  const fullWidthLayout = useUIStore((s) => s.fullWidthLayout);
  const toggleFullWidthLayout = useUIStore((s) => s.toggleFullWidthLayout);
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const setMobileMenuOpen = useUIStore((s) => s.setMobileMenuOpen);

  const user = useAuthStore((s) => s.user);
  const canReadMessages = useAuthStore((s) => s.hasPermission('messages.read'));
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const visibleHeaderNav = headerNavItems.filter((item) =>
    isNavItemVisible(item, hasAnyPermission, HEADER_NAV_MODULES),
  );
  const signOut = useSignOut();
  const { data: unreadMessages } = useUnreadMessagesCount();
  const unreadMessagesTotal = unreadMessages?.total ?? 0;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [profileOpen, setProfileOpen] = useState(
    // A step-up came back (#1763). The dialog that started it was destroyed by
    // the navigation to Paya ID, so it has to be back on screen before
    // `MobileLoginCodeSection` can spend the grant — and the initial value,
    // not an effect, so the section mounts on the very first render and no
    // frame passes with the grant unclaimed.
    () => window.location.hash.startsWith('#stepUp='),
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    // Ends the OS Buddy session and the Paya ID session with it (#2050,
    // #2109). Deliberately fire-and-forget: the route guard moves the browser
    // to /login the moment the store clears, and the hook owns the rest.
    void signOut();
  };

  const sidebarWidth = isMobile
    ? 0
    : sidebarCollapsed
      ? SIDEBAR_COLLAPSED_WIDTH
      : SIDEBAR_WIDTH;

  // The assistant panel only reserves layout space when it's actually
  // rendered as a non-modal side-by-side panel (AssistantPanel.tsx uses the
  // same useAssistantSideBySide check) — on mobile, or a desktop window too
  // narrow to fit both usably, it falls back to a temporary overlay that
  // doesn't affect this width.
  const isAssistantOpen = useAssistantStore((s) => s.isOpen);
  const assistantSideBySide = useAssistantSideBySide(sidebarWidth);
  const assistantWidth = isAssistantOpen && assistantSideBySide ? ASSISTANT_PANEL_WIDTH : 0;

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{
        // MUI's fixed AppBar is right-anchored (left: auto; right: 0), so both
        // edges must be pinned explicitly: a shrinking width calc would slide
        // the whole bar rightwards under the assistant panel instead of making
        // room for it (margin-left has no effect on an over-constrained
        // fixed-position box).
        left: `${sidebarWidth}px`,
        right: `${assistantWidth}px`,
        width: 'auto',
        // Top bar matches the sidebar's darkest chrome in dark mode; light mode
        // keeps the inherited paper surface.
        ...(theme.palette.mode === 'dark' && { backgroundColor: darkSurfaces.sidebar }),
        // Fixed HEADER_HEIGHT; the top divider is drawn once in AppShell as a
        // fixed full-width line, so no per-component border here.
        height: HEADER_HEIGHT,
        boxSizing: 'border-box',
        transition: theme.transitions.create(['left', 'right'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Toolbar
        variant="dense"
        sx={{ minHeight: '100% !important', height: '100%' }}
      >
        {/* Mobile menu button */}
        {isMobile && (
          <Tooltip title={t('openMenu')}>
            <IconButton
              edge="start"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ mr: 2 }}
              aria-label={t('openMenu')}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Global Search */}
        <GlobalSearch />

        <Box sx={{ flexGrow: 1 }} />

        {/* Clock In/Out */}
        <ClockInButton />

        {/* Inbound documents and exceptions — moved out of the sidebar (#3599). */}
        {visibleHeaderNav.map((item) => {
          const Icon = item.icon;
          const label = tn(item.labelKey);
          const active =
            location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
          return (
            <Tooltip key={item.path} title={label}>
              <IconButton
                onClick={() => {
                  navigateAwayFromWorkOrderDetail(location.pathname, item.path, navigate);
                }}
                sx={{ ml: 1, ...(active ? { color: 'primary.main' } : {}) }}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
              >
                <Icon />
              </IconButton>
            </Tooltip>
          );
        })}

        {/* Messages unread badge (PF-113) — desktop only; mobile uses the bottom nav. */}
        {!isMobile && canReadMessages && (
          <Tooltip title={tm('unreadBadge', { count: unreadMessagesTotal })}>
            <IconButton
              sx={{ ml: 1 }}
              aria-label={tm('unreadBadge', { count: unreadMessagesTotal })}
              onClick={() => navigate('/messages')}
            >
              <Badge badgeContent={unreadMessagesTotal} color="error">
                <ChatIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {/* Second work order window for a two-screen desk (#3653) — desktop only. */}
        <WorkOrderWindowControl />

        <NotificationsMenu />

        {/* CoPaya menu (#1806) — self-gated on the integration being configured */}
        <CopayaMenu />

        {/* AI Assistant toggle */}
        <AssistantToggleButton />

        {/* User menu */}
        <ButtonBase
          onClick={handleMenuOpen}
          sx={{ ml: 2, borderRadius: '50%', p: 0.25 }}
          aria-label={t('openUserMenu')}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
        >
          <UserAvatar
            firstName={user?.firstName ?? ''}
            lastName={user?.lastName ?? ''}
            avatarUrl={user?.avatarUrl}
            size={36}
            alt={`${user?.firstName} ${user?.lastName}`}
          />
        </ButtonBase>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          disableScrollLock
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: { width: 260, mt: 1 },
          }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <UserAvatar
              firstName={user?.firstName ?? ''}
              lastName={user?.lastName ?? ''}
              avatarUrl={user?.avatarUrl}
              size={36}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </Box>
          <Divider />
          <MenuItem onClick={() => { handleMenuClose(); setProfileOpen(true); }}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            {t('profile.title')}
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            {t('settings')}
          </MenuItem>
          {staffHelpEntries(i18n.language, support).map((entry) => {
            const Icon = HELP_MENU_ICONS[entry.kind];
            return (
              <MenuItem
                key={entry.kind}
                component="a"
                href={entry.href}
                {...(entry.newTab
                  ? { target: '_blank' as const, rel: 'noopener noreferrer' }
                  : {})}
                onClick={handleMenuClose}
              >
                <ListItemIcon>
                  <Icon fontSize="small" />
                </ListItemIcon>
                {tn(entry.labelKey)}
              </MenuItem>
            );
          })}
          <MenuItem onClick={(e) => setLangAnchorEl(e.currentTarget)}>
            <ListItemIcon>
              <LanguageIcon fontSize="small" />
            </ListItemIcon>
            {t('language')}: {languageNames[i18n.language as SupportedLanguage] ?? i18n.language}
          </MenuItem>
          <MenuItem onClick={toggleThemeMode}>
            <ListItemIcon>
              <HighBeamIcon fontSize="small" />
            </ListItemIcon>
            {themeMode === 'light' ? t('switchToDarkMode') : t('switchToLightMode')}
          </MenuItem>
          {!isMobile && (
            <MenuItem onClick={toggleFullWidthLayout}>
              <ListItemIcon>
                {fullWidthLayout ? (
                  <FitScreenIcon fontSize="small" />
                ) : (
                  <OpenInFullIcon fontSize="small" />
                )}
              </ListItemIcon>
              {fullWidthLayout ? tc('layout.constrained') : tc('layout.fullWidth')}
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {t('logout')}
          </MenuItem>
        </Menu>
      </Toolbar>

      {/* Language sub-menu */}
      <Menu
        anchorEl={langAnchorEl}
        open={Boolean(langAnchorEl)}
        onClose={() => setLangAnchorEl(null)}
        disableScrollLock
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        {uiLanguages.map((lng) => (
          <MenuItem
            key={lng}
            selected={i18n.language === lng}
            onClick={() => {
              void i18n.changeLanguage(lng);
              setLangAnchorEl(null);
              handleMenuClose();
            }}
          >
            {languageNames[lng]}
          </MenuItem>
        ))}
      </Menu>

      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </AppBar>
  );
});
